import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { IssueDetail, IssueSummary } from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { overlayDetail, overlayList, toSummary } from "../../offline/ops";
import { queue } from "../../offline/queue";
import { useQueueState } from "../../offline/state";

export const issuesKey = (buildingId: string) => ["issues", buildingId] as const;
export const issueKey = (id: string) => ["issue", id] as const;

/*
 * In de cache staat altijd wat de server teruggaf. Wijzigingen die nog in de offline-wachtrij staan
 * worden er bij het tonen overheen gelegd (`select`), zodat ze ook na herladen zonder verbinding
 * zichtbaar blijven en nooit dubbel tellen.
 */

/** Bevindingen van een gebouw, plus wat nog in de offline-wachtrij staat. */
export function useIssues(buildingId: string, enabled = true) {
  const { version } = useQueueState();
  const select = useCallback(
    (list: IssueSummary[]) => overlayList(list, queue.mine(), buildingId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildingId, version],
  );
  return useQuery({
    queryKey: issuesKey(buildingId),
    queryFn: () => api<IssueSummary[]>(`/buildings/${buildingId}/issues`),
    select,
    // Zonder de module Bevindingen niet ophalen (de server weigert het).
    enabled,
  });
}

export function useIssue(id: string | null, buildingId?: string) {
  const qc = useQueryClient();
  const { version } = useQueueState();
  const select = useCallback(
    (d: IssueDetail | null) => overlayDetail(d ?? undefined, queue.mine(), id ?? ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, version],
  );
  return useQuery({
    queryKey: issueKey(id ?? ""),
    queryFn: async () => {
      try {
        return await api<IssueDetail>(`/issues/${id}`);
      } catch (e) {
        // Nieuw op dit apparaat en nog niet verstuurd: de lokale versie (uit de wachtrij) tonen.
        if (e instanceof ApiError && e.status === 404 && overlayDetail(undefined, queue.mine(), id!)) return null;
        throw e;
      }
    },
    enabled: Boolean(id),
    select,
    // Nog geen detail op dit apparaat (bv. offline): alvast de gegevens uit de lijst.
    placeholderData: () => {
      if (!id) return undefined;
      if (overlayDetail(undefined, queue.mine(), id)) return null;
      const s = buildingId ? qc.getQueryData<IssueSummary[]>(issuesKey(buildingId))?.find((i) => i.id === id) : null;
      return s ? ({ ...s, createdBy: null, photos: [], events: [] } as IssueDetail) : undefined;
    },
  });
}

/** Antwoord van de server in de cache: detail én de samenvatting in de lijst. */
export function putIssue(qc: QueryClient, buildingId: string, d: IssueDetail) {
  qc.setQueryData(issueKey(d.id), d);
  qc.setQueryData<IssueSummary[]>(issuesKey(buildingId), (list) => {
    if (!list) return list;
    const summary = toSummary(d);
    const idx = list.findIndex((i) => i.id === d.id);
    if (idx < 0) return [...list, summary];
    const next = list.slice();
    next[idx] = summary;
    return next;
  });
}

/** Na een mutatie: detail bijwerken en de bijbehorende samenvatting in de lijst vervangen. */
export function useIssueCache(buildingId: string) {
  const qc = useQueryClient();
  return {
    put: (d: IssueDetail) => putIssue(qc, buildingId, d),
    remove(id: string) {
      qc.removeQueries({ queryKey: issueKey(id) });
      qc.setQueryData<IssueSummary[]>(issuesKey(buildingId), (list) => list?.filter((i) => i.id !== id));
    },
  };
}
